import { AttributeFormatDc, AttributeType, PagedListFeatureDc } from "@evergis/api";
import { debounce } from "@evergis/sgis/es/utils/utils";
import { AutoComplete, Icon, IJSXOption, InfiniteScrollProps, IOption } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getAppConfig } from "../../../../../ducks/appConfig";
import { api } from "../../../../../evergis/api";
import { AttributesUpdate } from "../../../../../hooks/attributes/useAttributes";
import { usePostgresLayer } from "../../../../../hooks/layer/usePostgresLayer";
import { useNotification } from "../../../../../hooks/notifications/useNotification";
import { LayerInfo } from "../../../../../interfaces/resourceInfo";
import { formatAttributeValue } from "../../../../../utils/formatAttributeValue";
import { FeatureAttribute, FeatureAttributeValue } from "../FeatureAttributeField";
import { OptionDescription } from "./styled";

export interface AttributeValueSelectorProps {
  layer: LayerInfo;
  name: string;
  alias: string;
  value: FeatureAttributeValue;
  stringFormat?: AttributeFormatDc;
  attributeType: AttributeType;
  updateAttribute: AttributesUpdate<FeatureAttribute>["updateAttribute"];
  storeAttributes: VoidFunction;
}

const DEFAULT_LIMIT = 40;
const DEBOUNCE_TIMER = 500;

// eslint-disable-next-line max-lines-per-function
export const AttributeValueSelector: FC<AttributeValueSelectorProps> = ({
  name,
  layer,
  alias,
  value,
  stringFormat,
  attributeType,
  updateAttribute,
  storeAttributes,
}) => {
  const { t } = useTranslation("common");
  const changeFromSelect = useRef(false);
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState<PagedListFeatureDc>();
  const [inputValue, setInputValue] = useState(formatAttributeValue(attributeType, value, stringFormat));
  const { settings } = useSelector(getAppConfig);
  const layerReference = useMemo(() => {
    const attributeConfiguration = layer?.configuration?.attributesConfiguration?.attributes.find(
      ({ attributeName }) => attributeName === name,
    );
    const layerReferences = layer?.configuration?.attributesConfiguration?.layerReferences;

    return layerReferences.find(({ referenceId }) => attributeConfiguration?.layerReferenceId?.includes(referenceId));
  }, [layer, name]);
  const {
    postgresLayer: referenceLayer,
    loading: referenceLayerLoading,
    error: referenceLayerError,
  } = usePostgresLayer(layerReference.layerName);
  const onSelect = useCallback(
    (option: IJSXOption) => {
      changeFromSelect.current = true;
      updateAttribute(name, { value: option.value });
    },
    [name, updateAttribute],
  );

  const onGetOptions = useCallback(
    async (search: string, clearPrevious?: boolean) => {
      let options: IOption<string>[] = [];

      if (clearPrevious) {
        setCurrentPage(void 0);
        setItems([]);
        updateAttribute(name, { value: "" });
      }

      if (referenceLayerLoading || referenceLayerError) {
        return options;
      }

      const referenceAttributesConfiguration = referenceLayer.layerDefinition?.attributes;
      const referenceAttributeType = referenceAttributesConfiguration?.[layerReference.referenceAttribute]?.type;
      const referenceTitleAttribute = referenceLayer.layerDefinition?.titleAttribute;
      const referenceTitleAttributeFormat = referenceAttributesConfiguration?.[referenceTitleAttribute]?.stringFormat;
      const limit = settings?.layer?.maxHandbookRecords || DEFAULT_LIMIT;
      const oneAttribute = referenceTitleAttribute === layerReference.referenceAttribute;

      const features = await api.layers.featuresContains({
        name: layerReference.layerName,
        offset: currentPage && !clearPrevious ? currentPage?.offset + limit : 0,
        limit,
        withGeom: false,
        filter: search,
        filterAttributes: oneAttribute
          ? [layerReference.referenceAttribute]
          : [referenceTitleAttribute, layerReference.referenceAttribute].filter(Boolean),
      });

      setCurrentPage(features);

      options = features.items.map(({ attributes }) => {
        const titleAttribute = attributes[referenceTitleAttribute];

        return {
          text: formatAttributeValue(attributeType, attributes[layerReference.referenceAttribute], stringFormat),
          value: String(attributes[layerReference.referenceAttribute]),
          suffix:
            titleAttribute && !oneAttribute ? (
              <OptionDescription>
                {formatAttributeValue(referenceAttributeType, titleAttribute, referenceTitleAttributeFormat)}
              </OptionDescription>
            ) : (
              ""
            ),
        };
      });

      setItems(currentItems => [...currentItems, ...options]);
    },
    [
      referenceLayerLoading,
      referenceLayerError,
      referenceLayer.layerDefinition?.attributes,
      referenceLayer.layerDefinition?.titleAttribute,
      layerReference.referenceAttribute,
      layerReference.layerName,
      settings?.layer?.maxHandbookRecords,
      currentPage,
      updateAttribute,
      name,
      attributeType,
      stringFormat,
    ],
  );
  const debouncedGetOptions = useMemo(() => debounce(onGetOptions, DEBOUNCE_TIMER), [onGetOptions]);
  const onChange = useCallback(
    async (newValue: string) => {
      setInputValue(newValue);

      setTimeout(() => {
        if (changeFromSelect.current) {
          changeFromSelect.current = false;
        } else {
          debouncedGetOptions(newValue.trim(), true);
        }
      }, 0);
    },
    [debouncedGetOptions],
  );
  const loadNext = useCallback(() => {
    onGetOptions(inputValue);
  }, [onGetOptions, inputValue]);
  const infiniteScrollProps = useMemo(
    (): InfiniteScrollProps => ({
      loadNext,
      hasMore: currentPage?.totalCount > currentPage?.offset + currentPage?.limit,
    }),
    [loadNext, currentPage],
  );

  useEffect(() => {
    if (value) {
      setInputValue(formatAttributeValue(attributeType, value, stringFormat));
    }
  }, [value]);

  useEffect(() => {
    if (name && referenceLayer && !referenceLayerLoading && !referenceLayerError) {
      onGetOptions("", true);
    }
  }, [name, referenceLayer, referenceLayerLoading, referenceLayerError]);

  useNotification(referenceLayerError, {
    error: true,
    title: t("dictionariesLoadingError"),
    description: `${t("referenceLayerError")}: ${layerReference.layerName}`,
  });

  return (
    <AutoComplete
      label={alias || name}
      options={items}
      filterOptions={(_, options) => options}
      value={inputValue || ""}
      onChange={onChange}
      onSelect={onSelect}
      zIndex={9999}
      onBlur={storeAttributes}
      width={"100%"}
      menuWidth={"20.5rem"}
      menuHeight={"25rem"}
      optionStyle={{ width: "16.5rem" }}
      withInfiniteScroll
      infiniteScrollProps={infiniteScrollProps}
      // eslint-disable-next-line react/no-children-prop
      children={<Icon kind={"suggestions"} />}
    />
  );
};
